<template>
  <div
    :class="['flex', wrapperClass, message.role === 'assistant' ? 'pb-12 mb-1' : 'mb-6']"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div :class="['relative flex flex-col', contentMaxWidth]">
      <div :class="['flex items-center mb-1.5 px-1', headerAlign]">
        <span class="text-nera-muted text-xs font-normal">{{ formatTime(message.created_at) }}</span>
      </div>

      <template v-if="Array.isArray(message.content)">
        <div v-for="(block, index) in message.content" :key="index">
          <div v-if="block.type === 'text'" :class="proseClasses" v-html="marked.parse(block.content)"></div>
          <Table v-else-if="block.type === 'table'" :tableId="block.id" :tables="message.metadata?.tables || []" />
          <ChartDisplay
            v-else-if="block.type === 'chart'"
            :chartId="block.id"
            :chartPaths="message.metadata?.chart_paths || []"
            class="my-2"
          />
          <div v-else :class="['rounded-[25px] leading-[1.5] w-fit prose prose-invert max-w-none', bubbleClass]">
            <em>Unsupported content type: {{ block.type }}</em>
          </div>
        </div>
      </template>
      <template v-else-if="message.content?.type === 'text'">
        <div :class="['rounded-[25px] leading-[1.5] w-fit prose prose-invert max-w-none', bubbleClass]" v-html="message.content.content"></div>
      </template>
      <template v-else>
        <div :class="['rounded-[25px] leading-[1.5] w-fit prose prose-invert max-w-none', bubbleClass]">
          {{ message.content }}
        </div>
      </template>

      <MessageStatus
        v-if="message.role === 'user' && message.status"
        :status="message.status"
        :error="message.error"
        :retryCount="message.retryCount || 0"
        :onRetry="() => retryMessage(message.id)"
      />

      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        leave-active-class="transition-opacity duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2.5"
        leave-to-class="opacity-0"
      >
        <MessageFeedback
          v-if="message.role === 'assistant' && showFeedback"
          :messageId="message.id"
          :existingFeedback="message.feedback?.feedback"
          :existingReport="message.feedback?.report"
          class="absolute top-full left-0 right-0"
          @feedback-submitted="handleFeedbackSubmitted"
        />
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from "vue";
import { useConversationsStore } from "../stores/conversations";
import ChartDisplay from "./ChartDisplay.vue";
import Table from "./Table.vue";
import MessageStatus from "./MessageStatus.vue";
import MessageFeedback from "./MessageFeedback.vue";
import { marked } from "marked";

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  feedbackDelay: {
    type: Number,
    default: 400,
  },
});

const conversationsStore = useConversationsStore();
const showFeedback = ref(false);
let feedbackTimeout = null;

const wrapperClass = computed(() => {
  switch (props.message.role) {
    case 'user': return 'justify-end animate-slide-in-right';
    case 'assistant': return 'justify-start animate-slide-in-left';
    case 'system': return 'justify-center animate-fade-in';
    default: return '';
  }
});

const contentMaxWidth = computed(() => {
  switch (props.message.role) {
    case 'assistant': return 'max-w-full';
    case 'system': return 'max-w-[40%]';
    default: return 'max-w-[60%]';
  }
});

const headerAlign = computed(() =>
  props.message.role === 'user' ? 'justify-end' : 'justify-between'
);

const bubbleClass = computed(() => {
  switch (props.message.role) {
    case 'user':
      return 'px-[1.125rem] py-3.5 bg-nera-user-msg text-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] self-end';
    case 'assistant':
      return 'p-2 bg-nera-bg text-nera-text';
    case 'system':
      return 'px-[1.125rem] py-3.5 bg-nera-container text-nera-muted text-sm text-center';
    default:
      return 'px-[1.125rem] py-3.5 bg-nera-container text-nera-muted text-sm text-center';
  }
});

const proseClasses = computed(() => [
  'prose max-w-none text-nera-text',
  'prose-headings:font-semibold prose-headings:text-nera-text',
  'prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg',
  'prose-p:leading-[1.5] prose-p:my-3 prose-p:text-lg',
  // Links — brand blue with subtle underline
  'prose-a:text-nera-accent hover:prose-a:text-nera-accent-hover',
  'prose-a:no-underline prose-a:border-b prose-a:border-nera-accent/30',
  'hover:prose-a:border-nera-accent-hover prose-a:transition-all',
  // Emphasis
  'prose-strong:font-semibold prose-strong:text-nera-text',
  'prose-em:italic',
  // Inline code — subtle neutral pill (no shouty red)
  'prose-code:bg-nera-elevated-3 prose-code:text-nera-accent-dark',
  'prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md',
  'prose-code:text-[0.9em] prose-code:font-mono prose-code:font-medium',
  'prose-code:border prose-code:border-nera-border',
  "prose-code:before:content-[''] prose-code:after:content-['']",
  // Code blocks — kept dark for syntax-highlight readability
  'prose-pre:bg-gray-950 prose-pre:text-gray-100',
  'prose-pre:rounded-lg prose-pre:p-4 prose-pre:my-4',
  'prose-pre:border prose-pre:border-gray-800',
  'prose-pre:shadow-lg prose-pre:overflow-x-auto',
  // Lists — visible bullet/number markers
  'prose-ul:list-disc prose-ul:pl-6 prose-ul:my-3 prose-ul:space-y-2',
  'prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-3 prose-ol:space-y-2',
  'prose-li:leading-relaxed prose-li:text-nera-text',
  'marker:text-nera-accent',
  // Blockquotes — accent-tinted
  'prose-blockquote:border-l-nera-accent/50 prose-blockquote:bg-nera-elevated-4/40',
  'prose-blockquote:border-l-4 prose-blockquote:pl-4',
  'prose-blockquote:py-3 prose-blockquote:my-4',
  'prose-blockquote:italic prose-blockquote:text-nera-text',
  'prose-blockquote:rounded-r',
  // Horizontal rules
  'prose-hr:border-nera-border prose-hr:my-6',
]);

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const handleMouseEnter = () => {
  if (props.message.role !== "assistant") return;

  if (feedbackTimeout) {
    clearTimeout(feedbackTimeout);
  }

  feedbackTimeout = setTimeout(() => {
    showFeedback.value = true;
  }, props.feedbackDelay);
};

const handleMouseLeave = () => {
  if (feedbackTimeout) {
    clearTimeout(feedbackTimeout);
    feedbackTimeout = null;
  }
  showFeedback.value = false;
};

const retryMessage = (messageId) => {
  conversationsStore.retryFailedMessage(messageId);
};

const handleFeedbackSubmitted = ({ messageId, feedback, report }) => {
  conversationsStore.updateMessageFeedback(messageId, feedback, report);
};

onUnmounted(() => {
  if (feedbackTimeout) {
    clearTimeout(feedbackTimeout);
  }
});
</script>
